"use client";

import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  ViewModule as GridIcon,
  ViewList as ListIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  Tune as TuneIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

import { useProductContext } from "../../context/ProductContext";
import { useProductFilterEngine } from "../../context/useProductFilterEngine";
import ProductCard from "../common/ProductCard";
import { BranchProduct } from "../../types";

const FeaturedProductsSection: React.FC = () => {
  const { t } = useTranslation();

const {
  fetchProducts,
  fetchNextProducts, // ✅
  products: contextProducts,
  next: contextNext,
  loading: contextLoading,
  categories,
} = useProductContext();


  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const filterEngine = useProductFilterEngine({
    debounceMs: 300,
    onFilterChange: ({ filters, mode }) => {
      if (mode === "append") {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
          if (v) params.set(k, String(v));
        });
        fetchProducts(params, true);
      }
    },
  });

  const filteredProducts = contextProducts.filter((item: BranchProduct) => {
    const { search, category, price_min, price_max } = filterEngine.filters;

    if (search && !item.product.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (category && !item.product.categories.some(c => c.id.toString() === category)) return false;

    const price = item.price;
    if (price_min !== undefined && price < price_min) return false;
    if (price_max !== undefined && price > price_max) return false;

    return true;
  });

  // تحقق مما إذا كان يمكن تحميل المزيد
  useEffect(() => {
    if (contextNext && !contextLoading) {
      setShowLoadMore(true);
    } else {
      setShowLoadMore(false);
    }
  }, [contextNext, contextLoading]);

  // تحميل المزيد يدويًا
const handleLoadMore = async () => {
  if (!contextNext || contextLoading) return;
  await fetchNextProducts();
};

  // Infinite scroll
useEffect(() => {
  if (!loadMoreRef.current) return;

  let fetching = false;
  const observer = new IntersectionObserver(
    async ([entry]) => {
      if (entry.isIntersecting && contextNext && !contextLoading && !fetching) {
        fetching = true;
        await fetchNextProducts();
        fetching = false;
      }
    },
    { rootMargin: "100px" }
  );

  observer.observe(loadMoreRef.current);
  return () => observer.disconnect();
}, [contextNext, contextLoading]);

  const activeChips = filterEngine.getActiveFilters();

  return (
    <section className="py-6 md:py-8 bg-white min-h-screen" ref={containerRef}>
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            {t("products.title")}
          </h1>
          <p className="text-gray-500 mt-1 text-sm md:text-base">
            {t("products.subtitle")}
          </p>
        </div>

        {/* Filters - Desktop */}
        <div className="hidden md:grid grid-cols-1 lg:grid-cols-4 gap-3 mb-6">
          <div className="relative">
            <SearchIcon className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder={t("products.search_placeholder")}
              className="w-full pl-4 pr-10 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none transition-all"
              value={filterEngine.searchQuery}
              onChange={(e) => filterEngine.updateSearchQuery(e.target.value)}
            />
          </div>

          <div className="relative">
            <select
              className="w-full pl-4 pr-10 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none appearance-none cursor-pointer"
              value={filterEngine.filters.category || ""}
              onChange={(e) => filterEngine.updateFilter("category", e.target.value)}
            >
              <option value="">{t("products.allCategories")}</option>
              {categories?.map((cat: any) => (
                <option key={cat.id} value={cat.id.toString()}>
                  {cat.name}
                </option>
              ))}
            </select>
            <ExpandMoreIcon className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none" />
          </div>

          <input
            type="number"
            placeholder={`${t("cart.price")} ${t("common.min")}`}
            className="w-full px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none transition-all"
            onChange={(e) =>
              filterEngine.updateFilter("price_min", e.target.value ? Number(e.target.value) : undefined)
            }
          />

          <input
            type="number"
            placeholder={`${t("cart.price")} ${t("common.max")}`}
            className="w-full px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none transition-all"
            onChange={(e) =>
              filterEngine.updateFilter("price_max", e.target.value ? Number(e.target.value) : undefined)
            }
          />
        </div>

        {/* Controls Row */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {/* Mobile Filter Button */}
            <button
              onClick={() => setShowMobileFilters(true)}
              className="md:hidden flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
            >
              <TuneIcon sx={{ fontSize: 18 }} />
              {t("products.filters")}
              {activeChips.length > 0 && (
                <span className="bg-primary text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {activeChips.length}
                </span>
              )}
            </button>

            {/* Active Filters Count */}
            {activeChips.length > 0 && (
              <span className="text-xs text-gray-500">
                {activeChips.length} {t("products.activeFilters")}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">
              {filteredProducts.length} {t("cart.items")}
            </span>
            
            {/* View Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-1.5 rounded transition-colors ${viewMode === "grid" ? "bg-white text-primary shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                title={t("products.gridView")}
              >
                <GridIcon sx={{ fontSize: 20 }} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-1.5 rounded transition-colors ${viewMode === "list" ? "bg-white text-primary shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                title={t("products.listView")}
              >
                <ListIcon sx={{ fontSize: 20 }} />
              </button>
            </div>
          </div>
        </div>

        {/* Active Filter Chips */}
        {activeChips.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {activeChips.map((chip) => (
              <div
                key={chip.key}
                className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
              >
                <span>{chip.value}</span>
                <button
                  onClick={() => filterEngine.removeFilter(chip.key as any)}
                  className="hover:text-red-500 p-0.5"
                >
                  <ClearIcon sx={{ fontSize: 14 }} />
                </button>
              </div>
            ))}
            <button
              onClick={filterEngine.resetFilters}
              className="text-xs text-red-500 hover:text-red-600 font-medium px-2 py-1 hover:bg-red-50 rounded transition-colors"
            >
              {t("products.clearAll")}
            </button>
          </div>
        )}

        {/* Mobile Filter Drawer */}
        <AnimatePresence>
          {showMobileFilters && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/30 z-40 md:hidden"
                onClick={() => setShowMobileFilters(false)}
              />
              <motion.div
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 25 }}
                className="fixed bottom-0 left-0 right-0 bg-white rounded-t-xl z-50 shadow-2xl md:hidden max-h-[70vh] overflow-hidden"
              >
                <div className="p-4 border-b">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-gray-900">{t("products.filters")}</h3>
                    <button
                      onClick={() => setShowMobileFilters(false)}
                      className="p-1 hover:bg-gray-100 rounded-full"
                    >
                      <CloseIcon />
                    </button>
                  </div>
                </div>

                <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(70vh-80px)]">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("common.search")}
                    </label>
                    <div className="relative">
                      <SearchIcon className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                      <input
                        type="text"
                        placeholder={t("products.search_placeholder")}
                        className="w-full pl-4 pr-10 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg outline-none"
                        value={filterEngine.searchQuery}
                        onChange={(e) => filterEngine.updateSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("products.category")}
                    </label>
                    <select
                      className="w-full px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg outline-none"
                      value={filterEngine.filters.category || ""}
                      onChange={(e) => filterEngine.updateFilter("category", e.target.value)}
                    >
                      <option value="">{t("products.allCategories")}</option>
                      {categories?.map((cat: any) => (
                        <option key={cat.id} value={cat.id.toString()}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("cart.price")} {t("common.min")}
                      </label>
                      <input
                        type="number"
                        placeholder={t("checkout.currency")}
                        className="w-full px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg outline-none"
                        onChange={(e) =>
                          filterEngine.updateFilter("price_min", e.target.value ? Number(e.target.value) : undefined)
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t("cart.price")} {t("common.max")}
                      </label>
                      <input
                        type="number"
                        placeholder={t("checkout.currency")}
                        className="w-full px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg outline-none"
                        onChange={(e) =>
                          filterEngine.updateFilter("price_max", e.target.value ? Number(e.target.value) : undefined)
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="p-4 border-t bg-white">
                  <button
                    onClick={() => setShowMobileFilters(false)}
                    className="w-full py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
                  >
                    {t("common.apply")}
                  </button>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Products Display */}
        {filteredProducts.length > 0 ? (
          <>
            <motion.div
              layout
              className={
                viewMode === "grid"
                  ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 h-fit"
                  : "flex flex-col gap-4 h-fit"
              }
            >
              <AnimatePresence mode="popLayout">
                {filteredProducts.map((item) => (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ProductCard product={item} variant={viewMode} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>

            {/* Load More Section */}
            <div className="mt-8">
              {/* Infinite Scroll Trigger */}
              <div ref={loadMoreRef} className="h-4" />
              
              {/* Manual Load More Button - Shows when there are more pages */}
              {showLoadMore && (
                <div className="text-center mt-6">
                  <button
                    onClick={handleLoadMore}
                    disabled={contextLoading}
                    className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {contextLoading ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-primary"></div>
                        {t("common.loading")}...
                      </span>
                    ) : (
                      t("products.loadMore")
                    )}
                  </button>
                  
                  {/* Auto-load message */}
                  <p className="text-xs text-gray-400 mt-2">
                    {t("cart.items")} {filteredProducts.length} • {t("common.loadingMore")}...
                  </p>
                </div>
              )}

              {/* Loading Indicator */}
              {contextLoading && !showLoadMore && (
                <div className="flex items-center justify-center py-8">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent"></div>
                    <span className="text-sm text-gray-500">{t("common.loading")}...</span>
                  </div>
                </div>
              )}

              {/* End of Results */}
              {!contextNext && filteredProducts.length > 0 && (
                <div className="text-center py-8 border-t border-gray-100 mt-8">
                  <p className="text-gray-500 text-sm">
                    {t("cart.items")} {filteredProducts.length} • {t("products.productsFound")}
                  </p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SearchIcon className="text-gray-400 text-2xl" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t("products.noProducts")}</h3>
            <p className="text-gray-500 text-sm mb-6">{t("products.noProductsDesc")}</p>
            <button
              onClick={filterEngine.resetFilters}
              className="px-5 py-2 bg-primary text-white rounded-lg font-medium text-sm hover:bg-primary/90 transition-colors"
            >
              {t("products.clearFilters")}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedProductsSection;